import axios from "axios";
import { API_BASE_URI } from "../../../../redux/ApiCall";
import { UserAccess, UserRole } from "../../../../redux/UserRole/UserRole.type";
import { ApiCallState } from "../../../../redux/Utils";
import { Project } from "../../../../redux/Project/Project.type";
import { Store } from "../../../../redux/Store/Store.type";
import { defaultPermissionMatrix } from './permissions';
import { PermissionSettings } from './permissions/types';

export type UserRolePropType = {
  user_roles: ApiCallState<UserRole[]>;
  fetchUserRoles: Function;
};

export type AddUserRolePropType = {
  fetchUserRoles: Function;
  id?: number;
  fetchUserRole: Function;
  user_role: ApiCallState<UserRole>;
  projects: ApiCallState<Project[]>;
  fetchProjects: Function;
  stores: ApiCallState<Store[]>;
  fetchStores: Function;
};

export type ViewUserRolePropType = {
  id: number;
  fetchUserRole: Function;
  user_role: ApiCallState<UserRole>;
  projects: ApiCallState<Project[]>;
  fetchProjects: Function;
  stores: ApiCallState<Store[]>;
  fetchStores: Function;
};

export const POST = (data: any) =>
  axios.post(`${API_BASE_URI}/user-role`, data);

export const DELETE = (id: number) =>
  axios.delete(`${API_BASE_URI}/user-role/${id}`);

export const PUT = (data: any) => axios.put(`${API_BASE_URI}/user-role`, data);

/**
 * Get roles with user access permissions
 * @param user_accesses - Array of user access permissions
 * @returns Array of UserAccess with all features included
 */
export const getRoles = (user_accesses: UserAccess[]) => {
  let data: UserAccess[] = [];

  USER_ACCESSES.forEach((e) => {
    let found = user_accesses?.find(
      (user_access) => user_access.feature === e.name
    );
    if (found) {
      data.push({
        ...found,
        feature: e.name,
        url: e.url,
        group: e.group,
        key: data.length,
      });
    } else {
      data.push({
        feature: e.name,
        key: data.length,
        read: false,
        write: false,
        check: false,
        approve_special: [],
        entry_special: [],
        url: "",
        group: "",
        review_and_approve: false,
        review_and_authorize: false,
        accept: false,
        user_role_id: 0,
      });
    }
  });

  return data;
};

/**
 * Apply a role template to create user access permissions
 * @param templateName - Name of the template to apply
 * @returns Array of UserAccess with template permissions
 */
export const applyRoleTemplate = (templateName: string): UserAccess[] => {
  const template = defaultPermissionMatrix[templateName];
  if (!template) {
    console.warn(`Template "${templateName}" not found`);
    return getRoles([]);
  }

  return USER_ACCESSES.map((access, index) => {
    const permissions = template[access.name] || {};
    return {
      feature: access.name,
      url: access.url,
      group: access.group,
      read: permissions.read || false,
      write: permissions.write || false,
      review_and_approve: permissions.review_and_approve || false,
      review_and_authorize: permissions.review_and_authorize || false,
      accept: permissions.accept || false,
      check: permissions.check || false,
      entry_special: [],
      approve_special: [],
      key: index,
      user_role_id: 0,
      project_id: 0,
    };
  });
};

/**
 * Get available role templates
 * @returns Array of template names
 */
export const getAvailableTemplates = (): string[] => {
  return Object.keys(defaultPermissionMatrix);
};

/**
 * Get permissions for a specific feature from a template
 * @param matrix - Permission matrix
 * @param feature - Feature name
 * @returns Permission settings for the feature
 */
export const getPermissionsForFeature = (matrix: { [key: string]: PermissionSettings }, feature: string): PermissionSettings => {
  for (const [key, permissions] of Object.entries(matrix)) {
    if (feature.includes(key)) {
      return permissions;
    }
  }
  return {};
};

export { defaultPermissionMatrix };

export const USER_ACCESSES = [
  { name: "EVM Dashboard", url: "/evm-dashboard", group: "Project" },
  { name: "Dashboard", url: "/dashboard", group: "General" },
  { name: "Project", url: "/project", group: "Project" },
  { name: "Project Type", url: "/project/:type", group: "Project" },
  { name: "Contractor Summary", url: "/contractor-summary/:id/:tab", group: "Project" },
  { name: "Client Summary", url: "/client-summary/:id/:tab", group: "Project" },
  { name: "Master Schedule Summaries", url: "/master-schedule-summaries/:id/:tab", group: "Schedule" },
  { name: "Resource Approval", url: "/resource-approval/:id/:tab", group: "Schedule" },
  { name: "Site Handover", url: "/site-handover/:id/:tab", group: "Project" },
  { name: "Register Project", url: "/register-project", group: "Project" },
  { name: "Sub Contract List", url: "/sub-contract-list", group: "Contract" },
  { name: "Register Sub Contract", url: "/register-sub-contract", group: "Contract" },
  { name: "Subcontractor Selection", url: "/client-summary/:id/subcontractor-selection", group: "Contract" },
  { name: "Sub Contract Registry", url: "/client-summary/:id/sub-contract-registry", group: "Contract" },
  { name: "Project Tab", url: "/project/:id/:tab", group: "Project" },
  { name: "Report", url: "/report", group: "Reporting" },
  { name: "Register", url: "/register", group: "User Management" },
  { name: "Pre Contract", url: "/pre-contract", group: "Contract" },
  { name: "Standard", url: "/standard", group: "Documentation" },
  { name: "Master Schedule Tab", url: "/master-schedule/:id/:tab", group: "Schedule" },
  { name: "Mirab Report", url: "/mirab-report", group: "Reporting" },
  { name: "Task", url: "/task", group: "Project" },
  { name: "HR", url: "/hr", group: "Human Resource" },
  { name: "HR Detail", url: "/hr/:id", group: "Human Resource" },
  { name: "Procurement", url: "/procurement", group: "Procurement" },
  { name: "Inventory", url: "/inventory", group: "Inventory" },
  { name: "Tender", url: "/tender", group: "Procurement" },
  { name: "Tender Submission Tab", url: "/tender-submission/:id/:tab", group: "Procurement" },
  { name: "Database", url: "/database", group: "Database" },
  { name: "Database Tab", url: "/database/:tab", group: "Database" },
  { name: "Finance", url: "/finance", group: "Finance" },
  { name: "Site Visit", url: "/site-visit", group: "Site Management" },
  { name: "Fixed Asset", url: "/fixed-asset", group: "Fixed Asset" },
  { name: "Fixed Asset Tab", url: "/fixed-asset/:tab", group: "Fixed Asset" },
  { name: "Fixed Asset Received", url: "/fixed-asset-received", group: "Fixed Asset" },
  { name: "Fixed Asset Return", url: "/fixed-asset-return", group: "Fixed Asset" },
  { name: "Fixed Asset Transfer Out", url: "/fixed-asset-transfer-out", group: "Fixed Asset" },
  { name: "Fixed Asset Transfer In", url: "/fixed-asset-transfer-in", group: "Fixed Asset" },
  { name: "Fixed Asset Issue", url: "/fixed-asset-issue", group: "Fixed Asset" },
  { name: "Fixed Asset Transfer (E-E)", url: "/fixed-asset-transfer", group: "Fixed Asset" },
  { name: "Fuel Issue", url: "/fuel-issue", group: "Fixed Asset" },
  { name: "Fuel Received", url: "/fuel-receiving", group: "Fixed Asset" },
  { name: "Fixed Asset Report", url: "/fixed-asset-report", group: "Fixed Asset" },
  { name: "Fixed Asset Status", url: "/fixed-asset-status", group: "Fixed Asset" },
  { name: "Acquiring Status", url: "/acquiring-status", group: "Fixed Asset" },
  { name: "Master List", url: "/master-list", group: "Fixed Asset" },
  { name: "Store", url: "/store", group: "Procurement" },
  { name: "Fixed Asset Registry", url: "/fixed-asset-registry", group: "Fixed Asset" },
  { name: "Fixed Asset Registry Starting Balance", url: "/fixed-asset-registry-starting-balance", group: "Fixed Asset" },
];

